import { Component, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Moment } from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { concatMap, finalize, first, map } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { Permission } from 'src/app/common/enums/permissions';
import { getBase64 } from 'src/app/common/utils/getBase64';
import { AddWorkerFamilyMemberFilesRequest, WorkerFamilyMemberFileRequest } from 'src/app/contracts/requests/add-worker-family-member-files-request';
import { DeregistrateWorkerFamilyMemberInsurance } from 'src/app/contracts/requests/deregistrate-worker-family-member-insurance-request';
import { InsureWorkerFamilyMemberRequest } from 'src/app/contracts/requests/insure-worker-family-member-request';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerService } from 'src/app/data/worker.service';
import { KinshipDegreeEnum } from 'src/app/models/enums/kinship-degree-enum';
import { WorkerFileTypeEnum } from 'src/app/models/enums/worker-file-type-enum';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

interface FamilyMemberInsuranceFormComponentData {
  workerId: number;
  memberId: number;
  dateOfBirth: Date;
  insuranceApplicationDate: Date;
  insuranceDeregistrationDate?: Date | null;
  kinshipDegreeId: KinshipDegreeEnum;
  alreadySavedFiles: string[];
  permissions: string[];
}

@Component({
  selector: 'app-family-member-insurance-form',
  templateUrl: './family-member-insurance-form.component.html',
  styleUrls: ['./family-member-insurance-form.component.scss'],
  standalone: false
})
export class FamilyMemberInsuranceFormComponent {
  data = inject<FamilyMemberInsuranceFormComponentData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<FamilyMemberInsuranceFormComponent>>(MatDialogRef);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private dictionaryService = inject(DictionaryService);
  private workerService = inject(WorkerService);

  readonly maxFileCount = this.dictionaryService
    .getWorkerFileTypeById(WorkerFileTypeEnum.WorkerFamilyMember)
    .pipe(map((fileType) => fileType.CountLimit));

  numberOfDaysToDeregistration: number = -5;

  isUnderage: boolean = false;
  isChild: boolean = false;
  isInsured: boolean = false;
  isDeregistrated: boolean = false;
  canEditForm: boolean = true;

  familyMemberInsuranceFormGroup: UntypedFormGroup;

  files: WorkerFamilyMemberFileRequest[] = [];


  constructor() {
    const data = this.data;

    this.familyMemberInsuranceFormGroup = this.buildFamilyMemberInsuranceFormGroup();
    this.isInsured = !!data.insuranceApplicationDate;
    this.isDeregistrated = !!data.insuranceDeregistrationDate;
    this.canEditForm = !this.isInsured && !this.isDeregistrated;
    this.canEditForm = true;

    if (this.isInsured) {
      this.patchFamilyMemberInsuranceFormGroup(data.insuranceApplicationDate, data.insuranceDeregistrationDate);
    }

    this.isUnderage = !!this.data.dateOfBirth && new Date(this.data.dateOfBirth) > new Date().addYears(-18);
    this.isChild = this.data.kinshipDegreeId === KinshipDegreeEnum.Child;
  }

  get insuranceDeregistrationDate(): Date | null {
    return this.familyMemberInsuranceFormGroup.get('insuranceDeregistrationDate').value;
  }
  get insuranceApplicationDate(): Date {
    return this.familyMemberInsuranceFormGroup.get('insuranceApplicationDate').value;
  }
  get isStatementAccepted(): boolean {
    return this.familyMemberInsuranceFormGroup.get('isStatementAccepted').value;
  }

  submit() {
    if (
      this.familyMemberInsuranceFormGroup.invalid ||
      (!this.isUnderage && this.isChild && !this.files.length && !this.data.alreadySavedFiles.length)
    )
      return;

    this.spinner.show();

    if (this.isInsured && this.insuranceDeregistrationDate) {
      this.deregisterInsurance();
    } else {
      this.registerInsurance();
    }
  }

  private deregisterInsurance() {
    const request: DeregistrateWorkerFamilyMemberInsurance = {
      InsuranceDeregistrationDate: this.insuranceDeregistrationDate,
    };

    this.workerService
      .deregistrateWorkerFamilyMemberInsurance(this.data.workerId, this.data.memberId, request)
      .pipe(
        first(),
        concatMap(() => this.addWorkerFamilyMemberFiles()),
        finalize(() => this.spinner.hide()),
      )
      .subscribe(() => {
        this.snackbar.openSuccessSnackBar(Messages.SuccessfullyDeregistratedWorkerFamilyMemberInsurance);
        this.dialogRef.close(true);
      });
  }

  private registerInsurance() {
    const request: InsureWorkerFamilyMemberRequest = {
      InsuranceApplicationDate: this.insuranceApplicationDate,
    };

    this.workerService
      .insureWorkerFamilyMember(this.data.workerId, this.data.memberId, request)
      .pipe(
        first(),
        concatMap(() => this.addWorkerFamilyMemberFiles()),
        finalize(() => this.spinner.hide()),
      )
      .subscribe(() => {
        this.snackbar.openSuccessSnackBar(Messages.SuccessfullyInsuredWorkerFamilyMember);
        this.dialogRef.close(true);
      });
  }

  cancel = () => this.dialogRef.close();

  onOptionChange(files: File[]) {
    this.files = [];
    let counter = 0;

    files.forEach((file) => {
      getBase64(file, (base64) => {
        this.files.push({ OriginalName: file.name, FileContent: base64 });
        counter++;
      });
    });

    const interval = setInterval(() => {
      if (counter == files.length) {
        this.spinner.hide();
        clearInterval(interval);
      }
    }, 200);
  }

  validateDateTest = (date: Moment): boolean => {
    if (this.data.permissions.includes(Permission.ManageFamilyMemberInsuranceApplicationDate))
      return true;

    return !!date && date.isSameOrAfter(new Date().resetTime().getTime());
  }

  validateDeregistrationDateTest = (date: Moment): boolean => {
    const applicationDate = this.insuranceApplicationDate;
    if (!applicationDate) {
      return false;
    }

    return !!date && date.isAfter(applicationDate) && date.isSameOrAfter(new Date().addDays(this.numberOfDaysToDeregistration));
  }

  private addWorkerFamilyMemberFiles(): Observable<any> {
    if (!this.files?.length) return of(null);

    const request: AddWorkerFamilyMemberFilesRequest = {
      Files: this.files,
    };

    return this.workerService.addWorkerFamilyMemberFiles(this.data.workerId, this.data.memberId, request);
  }

  private buildFamilyMemberInsuranceFormGroup() {
    return this.formBuilder.group({
      insuranceApplicationDate: [null, Validators.required],
      insuranceDeregistrationDate: [null],
      isStatementAccepted: [false, Validators.requiredTrue],
    });
  }

  private patchFamilyMemberInsuranceFormGroup(insuranceApplicationDate: Date, insuranceDeregistrationDate?: Date) {
    this.familyMemberInsuranceFormGroup.patchValue({
      insuranceApplicationDate: insuranceApplicationDate,
      insuranceDeregistrationDate: insuranceDeregistrationDate,
      isStatementAccepted: true,
    });

    if (this.isInsured) {
      this.familyMemberInsuranceFormGroup.get('insuranceApplicationDate').disable();
      this.familyMemberInsuranceFormGroup.get('isStatementAccepted').disable();
      this.familyMemberInsuranceFormGroup.get('insuranceDeregistrationDate').setValidators(Validators.required);
      this.familyMemberInsuranceFormGroup.get('insuranceDeregistrationDate').updateValueAndValidity({ emitEvent: false });
    }

    if (this.isDeregistrated) {
      this.familyMemberInsuranceFormGroup.get('insuranceDeregistrationDate').disable();
    }

    if (!this.canEditForm) {
      this.familyMemberInsuranceFormGroup.get('isStatementAccepted').disable();
    }
  }
}
